import { Dispatch, forwardRef, Fragment, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  CardActionAreaProps,
  CardContent,
  cardContentClasses,
  Collapse,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import classNames from 'classnames';

import { Icon, IconButton } from '@components/base';

type CardWithExpandableAreaProps = {
  actions?: ReactNode;
  additionalInfo?: ReactNode;
  clickableArea?: 'button' | 'card';
  expandableContent: ReactNode;
  expanded?: boolean;
  heading: ReactNode;
  itemId?: string;
  setExpanded?: Dispatch<SetStateAction<boolean>>;
  subheading?: ReactNode;
};

const ActionArea = styled(({ onClick, ...props }: CardActionAreaProps) =>
  onClick ? <CardActionArea onClick={onClick} {...props} /> : <Fragment>{props.children}</Fragment>
)`
  &.expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .${cardContentClasses.root} {
    padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  }
`;

const DataTreeWrapper = styled('div')`
  background: ${({ theme }) => theme.palette.background.default};
  border-bottom-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-bottom-right-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border-top: none;
  border: ${({ theme }) => `thin solid ${theme.palette.divider}`};
`;

const CardWithExpandableArea = forwardRef<HTMLDivElement, CardWithExpandableAreaProps>(
  (
    {
      actions,
      additionalInfo,
      clickableArea = 'card',
      expandableContent,
      expanded: controlledExpanded,
      heading,
      itemId,
      setExpanded: controlledSetExpanded,
      subheading,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(controlledExpanded ?? false);

    const handleSetExpanded: Dispatch<SetStateAction<boolean>> = arg => {
      controlledSetExpanded?.(arg);
      setExpanded(arg);
    };

    return (
      <Card ref={ref}>
        <ActionArea
          {...(clickableArea === 'card'
            ? {
                'aria-label': expanded ? t('source.hide') : t('source.view'),
                onClick: () => handleSetExpanded(prev => !prev),
              }
            : {})}
          className={classNames({ expanded })}
        >
          <Box component={CardContent} p={1.5}>
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'
              minHeight={28}
            >
              <Stack alignItems='flex-start'>
                <Typography variant='h4'>{heading}</Typography>

                {subheading && <Typography variant='body2'>{subheading}</Typography>}

                {itemId && (
                  <Typography color='text.secondary' variant='caption'>
                    {itemId}
                  </Typography>
                )}

                {additionalInfo}
              </Stack>

              <Stack alignItems='flex-end'>
                {actions}
                {clickableArea === 'card' ? (
                  <Icon fontSize='small' name={expanded ? 'expandLess' : 'expandMore'} />
                ) : (
                  <IconButton
                    fontSize='small'
                    icon={expanded ? 'expandLess' : 'expandMore'}
                    onClick={() => handleSetExpanded(prev => !prev)}
                    title={expanded ? t('source.hide') : t('source.view')}
                  />
                )}
              </Stack>
            </Stack>
          </Box>
        </ActionArea>

        <Collapse in={expanded}>
          <DataTreeWrapper>{expandableContent}</DataTreeWrapper>
        </Collapse>
      </Card>
    );
  }
);

export default CardWithExpandableArea;
